import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import { PulseLoader } from "react-spinners";
import { Close as CloseIcon } from "@mui/icons-material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import swal from "sweetalert";
import { useTheme } from "@mui/material";
import { useQuery, useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import {
  GET_USERS,
  RESET_PASSWORD,
  ACTIVATE_USER,
  DEACTIVATE_USER,
  ACTIVATE_DEACTIVATE_USER,
  UNLOCK_USER,
} from "../../data/userData";
import UsersForm from "./users-form";
import AnchorTemporaryDrawer from "../../components/Drawer";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { transformRoleRights } from "../../data/RBAC/transformRoleRights";
import { AddUserButton, DynamicActions } from "../../data/RBAC/DynamicActions";
import Swal from "sweetalert2";
import { userManagementUrl } from "../../config";
import { SearchIcon } from "lucide-react";
import {
  fetchJson,
  fetchJsonDynamic,
  generateColumnsFromResults,
} from "../../data/DynamicServiceNew/jsondynamicservice";
import { generateColumnsFromQuery } from "../../data/Axios/DynamicService";
import { debounce } from "lodash";
import PerformanceForm from "../Targets/form";

function Users() {
  const theme = useTheme();
  const navigate = useNavigate();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isEditing, setIsEditing] = useState(false);
  const [Data, setData] = useState([]);
  const [editData, setEditData] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { roles, permissions } = transformRoleRights();
  const [searchTerm, setSearchTerm] = useState("");
  const [generatedColumn, setGeneratedColumn] = useState([]);
  const ignoreFields = [""];
  const { loading, error, data, refetch } = useQuery(GET_USERS);
  const [isTarget, setIsTarget] = useState(false);

  const [activateDeactivateUser] = useMutation(ACTIVATE_DEACTIVATE_USER, {
    onCompleted: () => {
      swal("Success!", "User status has been updated successfully", "success");
      refetch();
    },
    onError: () => {
      swal("Error!", "Unable to update user status, try again later", "error");
    },
  });

  const [unlockUser] = useMutation(UNLOCK_USER, {
    onCompleted: () => {
      swal(
        "Success!",
        "User lock status has been updated successfully",
        "success"
      );
      refetch();
    },
    onError: () => {
      swal(
        "Error!",
        "Unable to update user lock status, try again later",
        "error"
      );
    },
  });
  const [resetPassword] = useMutation(RESET_PASSWORD, {
    onCompleted: () => {
      swal("Success!", "User password has been reset successfully", "success");
    },
    onError: () => {
      swal("Error!", "Unable to reset password, try again later", "error");
    },
  });

  // Effect to update users state when query data changes
  useEffect(() => {
    if (data?.allUsersView?.results) {
      try {
        const parsedData = JSON.parse(data.allUsersView.results);
        if (Array.isArray(parsedData)) {
          setData(parsedData);
        } else {
          console.error("Parsed data is not an array:", parsedData);
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
  }, [data]);

  if (loading) return;
  // <CircularProgress />
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
    }}
  >
    <PulseLoader size={10} color={"#3f51b5"} />
  </Box>;
  if (error) return <p>Error: {error.message}</p>;

  const dataArray = JSON.parse(data.allUsersView.results);

  const handleEdit = (id) => {
    const userToEdit = dataArray.find((user) => user.Id === id)
      ? dataArray.find((user) => user.Id === id)
      : Data.find((user) => user.Id === id);
    setEditData(userToEdit);
    setIsEditing(true);
    if (isMobile) {
      setDialogOpen(true);
    } else {
      setDrawerOpen(true);
    }
  };

  const handleAddUser = () => {
    setIsEditing(false);
    setEditData(null);
    if (isMobile) {
      setDialogOpen(true);
    } else {
      setDrawerOpen(true);
    }
  };
  const columns = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.5,
      renderCell: (params) => params.api.getRowIndex(params.row.Id) + 1,
    },
    {
      field: "Username",
      headerName: "Username",
      flex: isMobile ? 0.5 : 1,
      minWidth: isMobile ? 100 : 100,
    },
    {
      field: "FirstName",
      headerName: "First Name",
      flex: isMobile ? 0.5 : 1,
      minWidth: isMobile ? 100 : 100,
    },
    {
      field: "LastName",
      headerName: "Last Name",
      flex: isMobile ? 0.5 : 1,
      minWidth: isMobile ? 100 : 100,
    },
    {
      field: "Email",
      headerName: "Email",
      flex: isMobile ? 0.5 : 1,
      minWidth: isMobile ? 100 : 100,
    },
    {
      field: "Phone",
      headerName: "Phone Number",
      flex: isMobile ? 0.5 : 1,
      minWidth: isMobile ? 100 : 100,
    },
    {
      field: "CreatedDate",
      headerName: "Date Created",
      flex: isMobile ? 0.5 : 1,
      minWidth: isMobile ? 100 : 100,
      valueFormatter: (params) => {
        if (!params.value) return "";
        const date = new Date(params.value);
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      },
    },

    {
      field: "Role",
      headerName: "Role",
      flex: isMobile ? 0.5 : 1,
      minWidth: isMobile ? 100 : 100,
    },
    {
      field: "Name",
      headerName: "Branch Name",
      flex: isMobile ? 0.5 : 1,
      minWidth: isMobile ? 100 : 100,
    },
    {
      field: "IsActive",
      headerName: "Status",
      flex: 0.5,
      renderCell: ({ row: { IsActive } }) => {
        const statusText = IsActive === 1 ? "Active" : "Inactive";
        const statusColor = IsActive === 1 ? "green" : "red";
        return (
          <Typography variant="body1" style={{ color: statusColor }}>
            {statusText}
          </Typography>
        );
      },
    },
    {
      field: "IsAccountLocked",
      headerName: "Lock Status",
      flex: 1,
      renderCell: ({ row: { IsAccountLocked } }) => {
        const statusText = IsAccountLocked === 0 ? "Unlocked" : "Locked";
        const statusColor = IsAccountLocked === 0 ? "green" : "red";
        return (
          <Typography variant="body1" style={{ color: statusColor }}>
            {statusText}
          </Typography>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: ({ row }) => (
        <Box>
          <DynamicActions
            id={row.Id}
            isActive={row.IsActive}
            isAccountLocked={row.IsAccountLocked}
            entity="Users"
            onEdit={handleEdit}
            onActivate={() =>
              handleActivate(row.Id, row.IsActive, row.FirstName, row.LastName)
            }
            setTarget={handleSetTarget}
            resetPassword={() => resetPassword({ variables: { id: row.Id } })}
            unlockUser={() =>
              handleUnlock(
                row.Id,
                row.IsAccountLocked,
                row.FirstName,
                row.LastName
              )
            }
          />
        </Box>
      ),
    },
  ];

  const handleSearch = async (input) => {
    // Update the search term state
    setSearchTerm(input);

    // Use generatedColumns if columns are undefined or null
    if (columns && Array.isArray(columns)) {
      if (input.trim() !== "") {
        // Filter the data based on the search input
        const filteredData = dataArray.filter((row) =>
          columns.some((column) => {
            const cellValue = row[column.field];
            return (
              typeof cellValue === "string" &&
              cellValue.toLowerCase().includes(input.toLowerCase())
            );
          })
        );

        // If no data matches the search term, fetch data from the server
        if (filteredData.length === 0) {
          try {
            if (input.length >= 10 && input.length <= 13) {
              const phoneNumberPattern = /^(07|254|\+254|01)\d{8,9}$/;

              if (phoneNumberPattern.test(input)) {
                // Fetch data dynamically
                const fetchedData = await fetchJsonDynamic(
                  "allUsersByColumnValues", // inputobjectname
                  "usermodel", // inputsubobjectname
                  { phone: input }, // parameters
                  userManagementUrl.uri // service URI
                );
                // Update the table with the fetched data
                setData(JSON.parse(fetchedData.results));
                setSearchTerm(null);
              } else {
                setData([]);
              }
            } else {
              const fetchedData = await fetchJsonDynamic(
                "allUsersByColumnValues", // inputobjectname
                "usermodel", // inputsubobjectname
                { username: input }, // parameters
                userManagementUrl.uri // service URI
              );
              setData(JSON.parse(fetchedData.results));
            }
          } catch (error) {
            console.error("Error fetching data:", error);
          }
        } else {
          // Update the data with filtered results
          setData(filteredData);
        }
      } else {
        // Clear the search term and refresh the table
        setData(dataArray);
      }
    } else {
      console.error(
        "Columns or generatedColumns are not available for search."
      );
    }
  };

  const debouncedSearch = debounce((input) => handleSearch(input), 0);
  const handleActivate = (id, isActive, firstName, lastName) => {
    const action = isActive ? "deactivate" : "activate";

    Swal.fire({
      title: `Are you sure you want to ${action} ${firstName} ${lastName}?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((willActivate) => {
      if (willActivate) {
        activateDeactivateUser({ variables: { id, isActive: !isActive } });
      }
    });
  };
  const handleUnlock = (id, isAccountLocked, firstName, lastName) => {
    const action = isAccountLocked === 0 ? "Lock" : "Unlock";

    Swal.fire({
      title: `Are you sure you want to ${action} ${firstName} ${lastName}?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((willActivate) => {
      if (willActivate) {
        unlockUser({ variables: { id, isAccountLocked: !isAccountLocked } });
      }
    });
  };
  const handleSetTarget = async (id) => {
    const QueryData = { fieldOfficerId: id };
    const userToEdit = await fetchJsonDynamic(
      "perfomanceTargetByColumnValues",
      "usermodel",
      QueryData,
      userManagementUrl.uri
    );
    if (userToEdit.results !== "[]") {
      setIsEditing(true);
      const editDataWithId = JSON.parse(userToEdit.results).map((item) => ({
        ...item,
        fieldofficerid: id,
      }));
      setEditData(editDataWithId);
    } else {
      localStorage.setItem("userToEdit", JSON.stringify(id)); // Save to localStorage
      setEditData([]);
      setIsEditing(false);
    }
    setIsTarget(true);
    // setEditData(userToEdit);
    // setIsEditing(true);
    if (isMobile) {
      setDialogOpen(true);
    } else {
      setDrawerOpen(true);
    }
    // setIsTarget(false);
  };

  const adjustedColumns = columns.map((col) => {
    if (isMobile) {
      if (
        !["Username", "FirstName", "IsActive", "actions"].includes(col.field)
      ) {
        return { ...col, hide: true }; // Hide non-essential columns on mobile
      }
    }
    return col;
  });

  return (
    <Box m="1rem">
      <Header title={"User Management"} subtitle={"Manage Users"} />
      <Box
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          // "& .MuiDataGrid-cell": {
          //   borderBottom: "none",
          // },
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid #808080",
            borderRight: "1px solid #808080",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
        }}
      >
        <Box display="flex" justifyContent="flex-end" mt={1}>
          <Box display="flex" justifyContent="flex-end" mt={1}>
            <TextField
              variant="outlined"
              size="medium"
              value={searchTerm}
              onChange={(e) => debouncedSearch(e.target.value)} // Call handleSearch on input change
              placeholder="Search User" // Use placeholder instead of label
              sx={{
                mr: 2,
                width: {
                  xs: "100%", // Full width on extra-small screens
                  sm: "100%", // 75% width on small screens
                  md: "100%", // 60% width on medium screens
                  lg: "400px", // 50% width on large screens
                  xl: "400px", // 40% width on extra-large screens
                },
                height: "40px",
                backgroundColor: colors.blueAccent[900],
                borderRadius: "25px", // Add border radius
                "& .MuiOutlinedInput-root": {
                  paddingLeft: "40px", // Add padding to prevent overlap with the icon
                  "& fieldset": {
                    border: "none", // Remove the border
                  },
                },
                "& .MuiInputAdornment-root": {
                  position: "absolute", // Position the icon absolutely
                  left: "10px", // Position icon within the field
                },
                "& .MuiInputBase-input": {
                  textAlign: "left", // Align text to the left
                  padding: "12px 0", // Adjust padding to align the text vertically
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <AddUserButton
            onClick={handleAddUser}
            entity={localStorage.getItem("SelectedRight")}
          />
        </Box>
        <DataGrid
          checkboxSelection
          rows={Data}
          columns={columns} // Use the adjusted columns with visibility conditions
          components={{ Toolbar: GridToolbar }}
          sx={{
            minWidth: isMobile ? "auto" : "auto",
            width: "100%",
            "& .MuiDataGrid-cell": {
              whiteSpace: "normal", // Allow text wrapping
              wordBreak: "break-word", // Ensure text breaks properly
              overflowWrap: "break-word",
              lineHeight: "normal",
              display: "flex",
              alignItems: "center",
            },
            "& .MuiDataGrid-row": {
              minHeight: "auto", // Adjust row height dynamically
              maxHeight: "unset",
            },
            // Light Mode Styling
            ...(theme.palette.mode === "light" && {
              "& .MuiDataGrid-row:nth-of-type(odd)": {
                backgroundColor: "#E0E0E0", // Light Gray
              },
              "& .MuiDataGrid-row:nth-of-type(even)": {
                backgroundColor: "#F5F5F5", // Slightly Lighter Gray
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#D6D6D6", // Hover Effect
              },
              "& .MuiDataGrid-cell": {
                color: "#000000", // Black text for better visibility
              },
              // "& .MuiDataGrid-columnHeaders": {
              //   backgroundColor: "#B0BEC5", // Light header color
              // },
            }),

            // Dark Mode Styling
            ...(theme.palette.mode === "dark" && {
              "& .MuiDataGrid-row:nth-of-type(odd)": {
                backgroundColor: "#333333", // Dark Gray
              },
              "& .MuiDataGrid-row:nth-of-type(even)": {
                backgroundColor: "#444444", // Slightly Lighter Gray
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#555555", // Hover Effect
              },
              "& .MuiDataGrid-cell": {
                color: "#FFFFFF", // White text for better contrast
              },
              // "& .MuiDataGrid-columnHeaders": {
              //   backgroundColor: "#222222", // Darker header color
              // },
            }),
          }}
          getRowId={(row) => row.Id}
        />
        {!isTarget ? (
          <AnchorTemporaryDrawer
            anchor="right"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            FormComponent={() => (
              <UsersForm
                onClose={() => setDrawerOpen(false)}
                isEditing={isEditing}
                userData={editData}
                refetch={refetch}
              />
            )}
          />
        ) : (
          <AnchorTemporaryDrawer
            anchor="right"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            FormComponent={() => (
              <PerformanceForm
                onClose={() => setDrawerOpen(false)}
                isEditing={isEditing}
                userData={editData}
                refetch={refetch}
              />
            )}
          />
        )}
        {isMobile && (
          <Dialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            fullScreen={isMobile}
          >
            <DialogTitle>
              <IconButton
                edge="end"
                color="inherit"
                onClick={() => setDialogOpen(false)}
                aria-label="close"
                sx={{ position: "absolute", right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <UsersForm
                onClick={() => setDialogOpen(false)}
                isEditing={isEditing}
                userData={editData}
                refetch={refetch}
              />
            </DialogContent>
          </Dialog>
        )}
      </Box>
    </Box>
  );
}

export default Users;
